import React, { useState, useMemo, useContext, useEffect } from 'react';
import { ModalContext } from '../../context/ModalContext.js';
import { useReactTable, getCoreRowModel, getPaginationRowModel, getFilteredRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { MdFullscreen, MdFullscreenExit } from 'react-icons/md';
import './TableView.css';

// Import GeoJSON data
import policies from '../../geojson/policies_all_ctus_counties.json';
import ExportCsv from './ExportCsv.js';

const transformGeoJSONToTableData = (filters, selectedLocation) => {
  const tableData = [];

  policies.features.forEach(feature => {
    const properties = feature.properties;
    const populationFilter = filters.communitySize[0]?.split('_')[0];

    // Check if we should include this feature based on selected location
    if (selectedLocation) {
      if (selectedLocation.type === 'city') {
        // For cities, match exact city name and ensure it's a CTU
        if (properties.name !== selectedLocation.name || properties.geog_type !== 'CTU') {
          return;
        }
      }
      if (selectedLocation.type === 'county') {
        // For counties, check if the geography type is 'County' and the county name matches
        if (properties.geog_type !== 'County' || 
            !properties.name.toLowerCase().includes(selectedLocation.name.toLowerCase())) {
          return;
        }
      }
    }

    // Apply population range filters from sliders
    let passesPopulationFilter = true;
    
    // Check city population filter (applies to CTUs/cities)
    if (filters.cityPopulation && filters.cityPopulation.minThreshold !== undefined && filters.cityPopulation.maxThreshold !== undefined) {
      if (properties.geog_type === 'CTU') {
        const maxThreshold = filters.cityPopulation.maxThreshold === 430000 ? 9999999 : filters.cityPopulation.maxThreshold;
        if (properties.pop20 < filters.cityPopulation.minThreshold || properties.pop20 > maxThreshold) {
          passesPopulationFilter = false;
        }
      }
    }
    
    // Check county population filter (applies to counties)
    if (filters.countyPopulation && filters.countyPopulation.minThreshold !== undefined && filters.countyPopulation.maxThreshold !== undefined) {
      if (properties.geog_type === 'County') {
        const maxThreshold = filters.countyPopulation.maxThreshold === 1400000 ? 9999999 : filters.countyPopulation.maxThreshold;
        if (properties.pop20 < filters.countyPopulation.minThreshold || properties.pop20 > maxThreshold) {
          passesPopulationFilter = false;
        }
      }
    }
    
    // Check legacy population range filter (applies to all)
    if (filters.populationRange && filters.populationRange.minThreshold !== undefined && filters.populationRange.maxThreshold !== undefined) {
      const maxThreshold = filters.populationRange.maxThreshold === 1400000 ? 9999999 : filters.populationRange.maxThreshold;
      if (properties.pop20 < filters.populationRange.minThreshold || properties.pop20 > maxThreshold) {
        passesPopulationFilter = false;
      }
    }

    // Apply other filters only if no specific location is selected, or apply them to the selected location's data
    if (passesPopulationFilter &&
        (filters.policyTypes.length == 0 || filters.policyTypes.includes(properties.category)) &&
        (filters.policySubtypes.length == 0 || filters.policySubtypes.includes(properties.subcat)) &&
        (filters.communitySize.length == 0 || Number.isNaN(Number.parseFloat(populationFilter)) || (properties.pop20 > populationFilter))) {
      properties.join_name = properties.name || 'N/A';
      properties.policy_nam = `${properties.policy_ct || 0} policies`;
      properties.pop20 = properties.pop20 || 0;
      properties.link = properties.mc_link || null;
      tableData.push(properties);
    }
  });

  return tableData;
};

const TableView = ({ filters, onFilterChange, onFilterMenuOpen, onClearFilters }) => {
  const { modals, closeModal, selectedLocation, clearSelectedLocation } = useContext(ModalContext);
  const [globalFilter, setGlobalFilter] = useState('');
  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(true);

  useEffect(() => {
    const tableData = transformGeoJSONToTableData(filters, selectedLocation);
    setData(tableData);
    
    // Apply default sorting only when viewing a specific city's policies
    if (selectedLocation && selectedLocation.type === 'city') {
      setSorting([{ id: 'category', desc: false }]); // Sort by Policy Type alphabetically
    } else {
      setSorting([]); // No default sorting for general table view
    }
  }, [filters, selectedLocation]);

  const handleFilterClick = () => {
    closeModal("tableView");
    if (onFilterMenuOpen) {
      onFilterMenuOpen();
    }
  };

  const handleClearFilters = () => {
    if (onClearFilters) {
      onClearFilters();
    }
  };

  const columns = useMemo(
    () => [
      {
        header: 'Community',
        accessorKey: 'name',
        sortingFn: 'alphanumeric',
        cell: info => {
          const mc_link = info.row.original.mc_link;
          return mc_link && mc_link !== "n/a" ? (
            <a 
              href={mc_link} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#2196f3', textDecoration: 'none' }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              {info.getValue()}
            </a>
          ) : info.getValue();
        }
      },
      {
        header: 'Number of Policies',
        accessorKey: 'policy_ct',
        sortingFn: (rowA, rowB) => {
          const a = parseInt(rowA.original.policy_ct) || 0;
          const b = parseInt(rowB.original.policy_ct) || 0;
          return a - b;
        }
      },
      {
        header: 'Policy Type',
        accessorKey: 'category',
        sortingFn: 'alphanumeric'
      },
      {
        header: 'Policy Subtype',
        accessorKey: 'subcat',
        sortingFn: 'alphanumeric',
        cell: info => {
          const plcy_link = info.row.original.plcy_link;
          return plcy_link && plcy_link !== "n/a" ? (
            <a 
              href={plcy_link} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#2196f3', textDecoration: 'none' }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              {info.getValue()}
            </a>
          ) : info.getValue();
        }
      },
      {
        header: 'Population (2020)',
        accessorKey: 'pop20',
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.pop20 || 0;
          const b = rowB.original.pop20 || 0;
          return a - b;
        },
        cell: info => info.getValue()?.toLocaleString() || 'N/A',
      }
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter,
      sorting,
    },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 50,
      },
    },
  });

  return (
    <div className={`modal ${modals.tableView ? 'block' : 'hidden'}`}>
      <div className={`table-container ${isFullscreen ? 'fullscreen' : ''}`}>
        <div className="table-header">
          <div className="table-title">
            <h4>
              {selectedLocation 
                ? `Policies for ${selectedLocation.type === 'county' 
                    ? `${selectedLocation.name.toLowerCase().charAt(0).toUpperCase() + selectedLocation.name.toLowerCase().slice(1)} County` 
                    : selectedLocation.name}`
                : 'Policy List'}
            </h4>
            <span className="record-count">
              Showing {table.getFilteredRowModel().rows.length} of {data.length} records
              {!selectedLocation && <i>. Note: Communities with no reported policies may be viewed at the end of this table.</i>}
            </span>
          </div>
          <div className="table-controls">
            <InputGroup className="search-box">
              <Form.Control
                type="text"
                value={globalFilter ?? ''}
                onChange={e => setGlobalFilter(e.target.value)}
                placeholder="Search all columns..."
              />
            </InputGroup>
            <button 
              className="fullscreen-button" 
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? "Exit Full Screen" : "Enter Full Screen"}
            >
              {isFullscreen ? (
                <>
                  <MdFullscreenExit />
                  <span>Minimize</span>
                </>
              ) : (
                <>
                  <MdFullscreen />
                  <span>Full Screen</span>
                </>
              )}
            </button>
          </div>
          <Button variant="close" onClick={() => closeModal("tableView")} />
        </div>

        <div className="table-responsive">
          <table className="policy-table">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th 
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{ cursor: 'pointer' }}
                      data-sort={header.column.getIsSorted()}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination-controls">
          <div className="page-info">
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
            <select
              id="pageSize"
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </div>
          <div className="page-buttons">
            <Button 
              variant="success" 
              onClick={handleFilterClick}
              style={{ marginRight: '0.5rem' }}
            >
              Filter
            </Button>
            <Button 
              variant="outline-secondary" 
              onClick={handleClearFilters}
              style={{ marginRight: '0.5rem' }}
            >
              Clear Filter
            </Button>
            <Button variant="outline-primary" onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>First</Button>
            <Button variant="outline-primary" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>Previous</Button>
            <Button variant="outline-primary" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>Next</Button>
            <Button variant="outline-primary" onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>Last</Button>
            <ExportCsv tableData={data} isTableView={true} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableView;
